import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from '../firebase/auth';
import db, { doc, getDoc, updateDoc, serverTimestamp } from '../firebase/firestore';
import { hasPermission } from '../utils/permissions';
import { normalizeAcademyType, isAgencyType } from '../utils/tenantType';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [academySuspended, setAcademySuspended] = useState(false);
  const [academyType, setAcademyType] = useState(null);
  const [academyCurrency, setAcademyCurrency] = useState(null);
  // ISO code — the default country for phone numbers (see PhoneInput).
  const [academyCountry, setAcademyCountry] = useState(null);
  const [portalAccess, setPortalAccess] = useState(null);
  const [createdByAgencyId, setCreatedByAgencyId] = useState(null);
  // Off by default (undefined on every academy predating this field) — the
  // floating feedback bubble (FeedbackButton.jsx) is only meant for
  // academies IAP is actively testing with or has asked for feedback, not
  // every tenant by default. Toggled per-academy from TenantDetail.jsx.
  const [feedbackButtonEnabled, setFeedbackButtonEnabled] = useState(false);
  // Same "off by default, toggled per-academy from TenantDetail.jsx" shape as
  // feedbackButtonEnabled above — gates the floating support-chat widget
  // (ChatWidgetButton.jsx), meant for an academy's first month rather than
  // every tenant permanently.
  const [chatWidgetEnabled, setChatWidgetEnabled] = useState(false);
  // Website lead bot add-on (functions/leadBot.js) — off by default, switched
  // on per academy by a super admin from TenantDetail.jsx.
  const [leadBot, setLeadBot] = useState({ enabled: false, cap: null, links: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        // Every step here is guarded: an unhandled rejection (a forced token
        // refresh or a read failing on a flaky connection) used to leave
        // `loading` true forever — a blank app until a manual reload.
        // Forced refresh keeps freshly-granted claims current; if it fails,
        // the cached token is still valid for the rest of this session.
        const tokenResult = await firebaseUser.getIdTokenResult(true)
          .catch(() => firebaseUser.getIdTokenResult());
        const academyId = tokenResult.claims.academyId;
        // Independent reads — run together rather than back to back.
        const [profile, academySnap] = await Promise.all([
          getDoc(doc(db, 'users', firebaseUser.uid)).catch(() => null),
          academyId ? getDoc(doc(db, 'academies', academyId)).catch(() => null) : Promise.resolve(null),
        ]);
        setUser(firebaseUser);
        setClaims(tokenResult.claims);
        setUserProfile(profile?.exists() ? profile.data() : null);

        // Check suspension for academy staff and record last login
        if (academyId) {
          const a = academySnap?.data() || {};
          setAcademySuspended(!!a.suspended);
          setAcademyType(normalizeAcademyType(a.type));
          setAcademyCurrency(a.currency || 'EUR');
          setAcademyCountry(a.country || null);
          setPortalAccess(a.portalAccess ?? null);
          setCreatedByAgencyId(a.createdByAgencyId ?? null);
          setFeedbackButtonEnabled(!!a.feedbackButtonEnabled);
          setChatWidgetEnabled(!!a.chatWidgetEnabled);
          setLeadBot({ enabled: a.leadBotEnabled === true, cap: a.leadBotMonthlyCap ?? null, links: a.bookingLinksEnabled === true });
          // Only an academy admin may write the academy doc — for players,
          // parents and coaches this was a guaranteed-to-fail write per login.
          if (tokenResult.claims.role === 'academyAdmin') {
            updateDoc(doc(db, 'academies', academyId), { lastLoginAt: serverTimestamp() }).catch(() => {});
          }
        } else {
          setAcademySuspended(false);
          setAcademyType(null);
          setAcademyCurrency(null);
          setAcademyCountry(null);
          setPortalAccess(null);
          setCreatedByAgencyId(null);
          setFeedbackButtonEnabled(false);
          setChatWidgetEnabled(false);
          setLeadBot({ enabled: false, cap: null, links: false });
        }
      } else {
        setUser(null);
        setClaims(null);
        setUserProfile(null);
        setAcademySuspended(false);
        setAcademyType(null);
        setPortalAccess(null);
        setCreatedByAgencyId(null);
        setFeedbackButtonEnabled(false);
        setChatWidgetEnabled(false);
        setLeadBot({ enabled: false, cap: null, links: false });
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const refreshClaims = async () => {
    if (user) {
      const tokenResult = await user.getIdTokenResult(true);
      setClaims(tokenResult.claims);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      const profile = await getDoc(doc(db, 'users', user.uid));
      setUserProfile(profile.exists() ? profile.data() : null);
    }
  };

  // Settings.jsx writes academy.currency directly (no claims involved), so
  // nothing else in AuthContext picks it up automatically — call this right
  // after saving so every other open tab-of-this-session's `academyCurrency`
  // reflects the change without a full reload.
  const refreshAcademy = async () => {
    const currentAcademyId = claims?.academyId;
    if (!currentAcademyId) return;
    const academySnap = await getDoc(doc(db, 'academies', currentAcademyId));
    setAcademyCurrency(academySnap.data()?.currency || 'EUR');
    setAcademyCountry(academySnap.data()?.country || null);
  };

  const value = {
    user,
    claims,
    userProfile,
    loading,
    refreshClaims,
    refreshProfile,
    refreshAcademy,
    role: claims?.role ?? null,
    academyId: claims?.academyId ?? null,
    isSuperAdmin: claims?.role === 'superAdmin',
    // IAP's own platform-level sales staff — not to be confused with an
    // academy's own salesRep (academy-scoped, works CRM leads for one
    // tenant). iapSales has no academyId, same as superAdmin, but can only
    // create academies/agencies and see the tenant list (see router.jsx's
    // RequireAuth allowedRoles and firestore.rules' isIapSales()) — no
    // delete, no Settings, no tenant-detail management actions.
    isIapSales: claims?.role === 'iapSales',
    isAcademyAdmin: claims?.role === 'academyAdmin',
    isGuardian: claims?.role === 'guardian',
    isSalesRep: claims?.role === 'salesRep',
    isCoach: claims?.role === 'coach',
    // Which tenant type the signed-in staff member's academyId belongs to —
    // 'academy' (default, including every tenant predating this field) or
    // 'agency' (e.g. PLYRMATCH — recruits for partner academies rather than
    // running its own Academy Portal). See utils/tenantType.js.
    academyType,
    isAgencyTenant: isAgencyType(academyType),
    // The academy's default currency for everything money-related (contract
    // vars, invoices/quotes, the CRM dashboard) — 'EUR' for any tenant that
    // predates this field. See Settings.jsx's Currency field.
    academyCurrency,
    academyCountry,
    // { crm, academyPortal } — a tenant-level ceiling on top of any
    // individual staff member's own access claim, set false for a
    // partner academy at creation (see createPartnerAcademy) until an IAP
    // super admin lifts it once it's paid for (TenantDetail.jsx's Portal
    // Access card, a direct Firestore write — same pattern as suspending a
    // tenant). Missing entirely (every tenant predating this field, and any
    // directly-onboarded academy) means both stay allowed.
    portalAccess,
    // Which of IAP's three portals this tenant is licensed for — set by a
    // super admin (CreateAcademy / TenantDetail → updateTenantPortals). A
    // missing key means "on" (every tenant predating the field). `access`
    // below is filtered against this, so a switched-off portal disappears
    // for everyone at the academy regardless of their own claim.
    tenantPortals: {
      crm: portalAccess?.crm !== false,
      onboarding: portalAccess?.onboarding !== false,
      academyPortal: portalAccess?.academyPortal !== false,
    },
    // The first user of an Academy-Portal-only club (see createInvite's
    // isHeadCoach) — manages Club settings and the club's other coaches.
    isHeadCoach: claims?.role === 'coach' && claims?.headCoach === true,
    feedbackButtonEnabled,
    chatWidgetEnabled,
    leadBotEnabled: leadBot.enabled && !isAgencyType(academyType),
    leadBotMonthlyCap: leadBot.cap,
    // Booking links add-on (/book/:slug) — separate from the lead bot, but
    // shares its call settings, availability and booked calls.
    bookingLinksEnabled: !!leadBot.links && !isAgencyType(academyType),
    // Set only for a tenant an agency provisioned via createPartnerAcademy
    // — provenance, not a right in itself (see updatePartnerAcademyDetails,
    // which checks it server-side for exactly that reason). Used here only
    // to know whether portalAccess.fullOnboarding is even a meaningful
    // question for this tenant — a directly-onboarded academy was never
    // restricted in the first place.
    createdByAgencyId,
    isPartnerAcademy: !isAgencyType(academyType) && !!createdByAgencyId,
    // A partner academy starts "onboarding-lite" — sees only the players
    // PLYRMATCH actually placed with it, can't add its own, and doesn't get
    // the Visits/Travel Forms/Webinars tabs on Calendar (paid-license
    // features) — until an IAP super admin flips portalAccess.fullOnboarding
    // (TenantDetail.jsx). Any non-partner tenant (including the agency
    // itself) is unaffected by this flag entirely.
    hasFullOnboarding: !(!isAgencyType(academyType) && !!createdByAgencyId) || portalAccess?.fullOnboarding !== false,
    // Visits/Travel Forms/Webinars specifically — off for the agency tenant
    // itself too (they're physical-academy concepts that don't apply to a
    // reseller), not just an un-upgraded partner.
    hasLicensedCalendarFeatures: !isAgencyType(academyType) && (!createdByAgencyId || portalAccess?.fullOnboarding !== false),
    // Academy Portal management capability (create/edit groups, post
    // announcements, manage calendar) — a coach's whole account is scoped to
    // this, and an academyAdmin gets it too since Firestore rules already
    // grant them full academy-wide access via belongsToAcademy. Use this
    // instead of isCoach for any Academy Portal "can I manage this" check.
    // Excluded for an agency tenant's admin (no players to train/coach) and
    // for a partner academy whose portalAccess.academyPortal is still false.
    canManageAcademyPortal: (claims?.role === 'coach' || claims?.role === 'academyAdmin')
      && !isAgencyType(academyType)
      && portalAccess?.academyPortal !== false,
    staffRole: userProfile?.staffRole ?? null,
    // academyAdmin implicitly has full access to both sections even for
    // accounts created before the "access" claim existed — no backfill
    // needed. salesRep access is explicit, granted per-user by an admin.
    // Guardian accounts default to the (pre-existing) Player Portal for the
    // same reason — only accounts explicitly granted 'academyPortal' (new
    // Academy Portal invites, or an admin adding it later) see the switcher.
    // 'crm' is then dropped again if the tenant's own portalAccess.crm is
    // false — a partner academy's own staff can't grant each other CRM
    // access around that ceiling, even via the Team Members access editor.
    // Likewise 'onboarding' (and the guardian Player Portal, which is part
    // of Onboarding) and 'academyPortal' drop out when IAP hasn't given the
    // tenant that portal.
    access: (claims?.access ?? (claims?.role === 'academyAdmin' ? ['onboarding', 'crm'] : claims?.role === 'guardian' ? ['playerPortal'] : []))
      .filter((a) => a !== 'crm' || portalAccess?.crm !== false)
      .filter((a) => (a !== 'onboarding' && a !== 'playerPortal') || portalAccess?.onboarding !== false)
      .filter((a) => a !== 'academyPortal' || portalAccess?.academyPortal !== false),
    academySuspended,
    // An agency tenant (e.g. PLYRMATCH) never gets Quotes — they collect a
    // flat registration fee via Invoices, not the estimate/proposal flow
    // quotes are for. Overriding it here (rather than per-staffRole in
    // DEFAULT_PERMISSIONS) means it's off for every staff member on an
    // agency tenant regardless of role, and the existing quotes/invoices
    // merge logic (Sidebar's resolveNavItem, QuotesInvoices.jsx's tab
    // redirect) already does the rest — same mechanism as a staff member
    // who's been granted Invoices but not Quotes.
    can: (permission) => (permission === 'quotes' && isAgencyType(academyType) ? false : hasPermission(userProfile, permission)),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
